import React from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import Svg, { Circle, Defs, LinearGradient, Path, Rect, Stop } from 'react-native-svg';

import CloseIcon from '../../../assets/dailyMissions/close.svg';
import { goldGlow } from '../../../shared/glowStyles';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { Pressable } from '../common/Touchable';

/**
 * The red panel the redesigned popups share (Daily Missions, checkout, payment result): a
 * top-to-bottom red fill, a bright gold rim with an orange bloom, and the round gold-rimmed close
 * button. The older popups (SvaraModal and the ones that draw their own frame) take it from here.
 */
export const redesignPanelColors = {
	backdrop: 'rgba(0,0,0,0.68)',
	rim: '#FFF908',
	base: '#320400',
	surface: '#1A0603',
	white: '#FFFFFF',
	title: '#FFE275',
	gold: '#FFC500',
	tileFill: '#330E07',
	tileBorder: '#DA600E',
	success: '#0CEB66',
	warning: '#FFC500',
	error: '#FF5A4A',
} as const;

const PANEL_FILL: Array<[string, string]> = [
	['0', '#770700'],
	['0.80127', '#320400'],
	['1', '#320400'],
];

/** The rim, base colour and bloom of the panel; the fill itself is `RedesignPanelFill`. */
export const redesignPanelFrame = (radius: number): ViewStyle => ({
	borderRadius: radius,
	borderWidth: 1.5,
	borderColor: redesignPanelColors.rim,
	// The gradient fill sits on top of this; the flat colour keeps the shadow layer opaque.
	backgroundColor: redesignPanelColors.base,
	shadowColor: '#FFB800',
	shadowOffset: { width: 0, height: 0 },
	shadowOpacity: 0.45,
	shadowRadius: 8,
	elevation: 12,
});

/** The panel's red gradient, laid behind its content. */
export const RedesignPanelFill = ({ radius }: { radius: number }) => {
	const fillId = useSvgId('redesignPanelFill');

	return (
		<View
			pointerEvents='none'
			style={[StyleSheet.absoluteFill, { borderRadius: radius, overflow: 'hidden' }]}
		>
			<Svg width='100%' height='100%'>
				<Defs>
					<LinearGradient id={fillId} x1='0' y1='0' x2='0' y2='1'>
						{PANEL_FILL.map(([offset, color]) => (
							<Stop key={offset} offset={offset} stopColor={color} />
						))}
					</LinearGradient>
				</Defs>
				<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${fillId})`} />
			</Svg>
		</View>
	);
};

type CloseButtonProps = {
	onPress: () => void;
	accessibilityLabel: string;
	/** Diameter of the visible circle; the hit area adds 12 around it. */
	size?: number;
	style?: StyleProp<ViewStyle>;
	disabled?: boolean;
};

/** The round close button pinned in a panel's top-right corner. */
export const RedesignCloseButton = ({
	onPress,
	accessibilityLabel,
	size = 32,
	style,
	disabled,
}: CloseButtonProps) => (
	<Pressable
		accessibilityRole='button'
		accessibilityLabel={accessibilityLabel}
		onPress={onPress}
		disabled={disabled}
		hitSlop={8}
		style={[closeStyles.hitArea, { width: size + 12, height: size + 12 }, style]}
	>
		<View style={[closeStyles.circle, { width: size, height: size, borderRadius: size / 2 }]}>
			<CloseIcon width={size * 0.56} height={size * 0.56} />
		</View>
	</Pressable>
);

const closeStyles = StyleSheet.create({
	hitArea: {
		position: 'absolute',
		top: 8,
		right: 8,
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 20,
	},
	circle: {
		borderWidth: 1,
		borderColor: redesignPanelColors.rim,
		backgroundColor: redesignPanelColors.surface,
		alignItems: 'center',
		justifyContent: 'center',
		...goldGlow,
	},
});

export type StatusKind = 'success' | 'cancelled' | 'error';

export const statusColor = (status: StatusKind) =>
	status === 'success'
		? redesignPanelColors.success
		: status === 'cancelled'
			? redesignPanelColors.warning
			: redesignPanelColors.error;

/** A status badge glyph, drawn on a 24 grid in the status colour. */
export const StatusGlyph = ({
	status,
	size,
	color,
}: {
	status: StatusKind;
	size: number;
	color: string;
}) => (
	<Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
		{status === 'success' ? (
			<Path
				d='M5.5 12.5l4.25 4.25L18.5 8'
				stroke={color}
				strokeWidth={2.6}
				strokeLinecap='round'
				strokeLinejoin='round'
			/>
		) : status === 'cancelled' ? (
			<>
				<Path
					d='M12 3.5L2.75 19.5h18.5L12 3.5z'
					stroke={color}
					strokeWidth={2.2}
					strokeLinejoin='round'
				/>
				<Path d='M12 9.5v4.5' stroke={color} strokeWidth={2.4} strokeLinecap='round' />
				<Circle cx={12} cy={16.9} r={1.35} fill={color} />
			</>
		) : (
			<Path d='M7 7l10 10M17 7L7 17' stroke={color} strokeWidth={2.6} strokeLinecap='round' />
		)}
	</Svg>
);

/** The round badge holding a `StatusGlyph`, ringed and lit in the status colour. */
export const StatusBadge = ({ status, size }: { status: StatusKind; size: number }) => {
	const color = statusColor(status);

	return (
		<View
			style={[
				badgeStyles.badge,
				{
					width: size,
					height: size,
					borderRadius: size / 2,
					borderWidth: Math.max(2, size * 0.04),
					borderColor: color,
					shadowColor: color,
					shadowRadius: size * 0.13,
				},
			]}
		>
			<StatusGlyph status={status} size={size * 0.53} color={color} />
		</View>
	);
};

const badgeStyles = StyleSheet.create({
	badge: {
		backgroundColor: redesignPanelColors.surface,
		alignItems: 'center',
		justifyContent: 'center',
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: 0.6,
		elevation: 8,
	},
});

/** A thin gold rule that fades out at both ends, for under a popup title. */
export const GoldDivider = ({ style }: { style?: StyleProp<ViewStyle> }) => {
	const lineId = useSvgId('goldDivider');

	return (
		<View pointerEvents='none' style={[dividerStyles.line, style]}>
			<Svg width='100%' height='100%'>
				<Defs>
					<LinearGradient id={lineId} x1='0' y1='0' x2='1' y2='0'>
						<Stop offset='0' stopColor='#E89200' stopOpacity={0} />
						<Stop offset='0.5' stopColor='#FFC500' stopOpacity={1} />
						<Stop offset='1' stopColor='#E89200' stopOpacity={0} />
					</LinearGradient>
				</Defs>
				<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${lineId})`} />
			</Svg>
		</View>
	);
};

const dividerStyles = StyleSheet.create({
	line: { height: 2, alignSelf: 'stretch' },
});
